import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { cn } from '../../lib/utils';
import { Card } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Badge } from '../../components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../../components/ui/table';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../../components/ui/alert-dialog';
import {
  BarChart,
  Bar,
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from 'recharts';
import { Wallet, Plus, Pencil, Trash2, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { Pagination } from '../../components/shared/Pagination';
import {
  cashflowApi,
  CashflowPeriod,
  CashflowProvider,
  CashflowSummary,
  CashReceipt,
  ProviderMetrics,
} from '../../api/cashflow';
import { ReceiptDialog, ReceiptFormData } from './ReceiptDialog';
import {
  formatMoney,
  parseMoney,
  formatPercent,
  PROVIDER_LABELS,
  PROVIDER_COLORS,
  PROVIDER_ORDER,
} from './utils';

interface CashflowPageProps {
  isDark: boolean;
}

const ITEMS_PER_PAGE = 10;

const PERIOD_OPTIONS: { value: CashflowPeriod; label: string }[] = [
  { value: 'all', label: 'Весь период' },
  { value: 'day', label: 'Сегодня' },
  { value: 'week', label: 'Неделя' },
  { value: 'month', label: 'Месяц' },
  { value: 'range', label: 'Диапазон' },
];

const today = () => new Date().toISOString().slice(0, 10);

const emptyForm = (): ReceiptFormData => ({
  date: today(),
  provider: 'click',
  amount: '',
  comment: '',
});

export default function CashflowPage({ isDark }: CashflowPageProps) {
  // Период
  const [period, setPeriod] = useState<CashflowPeriod>('all');
  const [rangeFrom, setRangeFrom] = useState('');
  const [rangeTo, setRangeTo] = useState('');

  // Сводка
  const [summary, setSummary] = useState<CashflowSummary | null>(null);
  const [summaryLoading, setSummaryLoading] = useState(true);

  // Таблица поступлений
  const [receipts, setReceipts] = useState<CashReceipt[]>([]);
  const [receiptsLoading, setReceiptsLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);
  const [providerFilter, setProviderFilter] = useState<'all' | CashflowProvider>('all');

  // Модалки
  const [isCreateOpen, setIsCreateOpen] = useState(false);
  const [isEditOpen, setIsEditOpen] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [formData, setFormData] = useState<ReceiptFormData>(emptyForm());
  const [deleteTarget, setDeleteTarget] = useState<CashReceipt | null>(null);

  const periodParams = useCallback(() => {
    const params: any = { period };
    if (period === 'range') {
      if (rangeFrom) params.from = rangeFrom;
      if (rangeTo) params.to = rangeTo;
    }
    return params;
  }, [period, rangeFrom, rangeTo]);

  const fetchSummary = useCallback(async () => {
    setSummaryLoading(true);
    try {
      const data = await cashflowApi.getSummary(periodParams());
      setSummary(data);
    } catch (error: any) {
      toast.error(error.response?.data?.message || 'Не удалось загрузить сводку');
    } finally {
      setSummaryLoading(false);
    }
  }, [periodParams]);

  const fetchReceipts = useCallback(async () => {
    setReceiptsLoading(true);
    try {
      const params: any = { ...periodParams(), page, limit: ITEMS_PER_PAGE };
      if (providerFilter !== 'all') params.provider = providerFilter;
      const data = await cashflowApi.getReceipts(params);
      setReceipts(data.items);
      setTotalPages(data.totalPages);
      setTotal(data.total);
    } catch (error: any) {
      toast.error(error.response?.data?.message || 'Не удалось загрузить поступления');
    } finally {
      setReceiptsLoading(false);
    }
  }, [periodParams, page, providerFilter]);

  useEffect(() => {
    fetchSummary();
  }, [fetchSummary]);

  useEffect(() => {
    fetchReceipts();
  }, [fetchReceipts]);

  const refreshAll = () => {
    fetchSummary();
    fetchReceipts();
  };

  // ─── handlers ──────────────────────────────────────────────────────
  const openCreate = () => {
    setFormData(emptyForm());
    setIsCreateOpen(true);
  };

  const openEdit = (r: CashReceipt) => {
    setEditingId(r.id);
    setFormData({
      date: r.date,
      provider: r.provider,
      amount: String(r.amount).replace('.', ','),
      comment: r.comment ?? '',
    });
    setIsEditOpen(true);
  };

  const validateForm = (): { date: string; provider: CashflowProvider; amount: number; comment?: string } | null => {
    if (!formData.date) {
      toast.error('Укажите дату');
      return null;
    }
    const amount = parseMoney(formData.amount);
    if (amount === null || amount <= 0) {
      toast.error('Сумма должна быть больше 0 (например 123 123,45)');
      return null;
    }
    return {
      date: formData.date,
      provider: formData.provider,
      amount,
      comment: formData.comment.trim() || undefined,
    };
  };

  const handleCreate = async () => {
    const dto = validateForm();
    if (!dto) return;
    try {
      await cashflowApi.createReceipt(dto);
      toast.success('Поступление добавлено');
      setIsCreateOpen(false);
      setPage(1);
      refreshAll();
    } catch (error: any) {
      toast.error(error.response?.data?.message || 'Не удалось добавить поступление');
    }
  };

  const handleUpdate = async () => {
    if (!editingId) return;
    const dto = validateForm();
    if (!dto) return;
    try {
      await cashflowApi.updateReceipt(editingId, dto);
      toast.success('Поступление обновлено');
      setIsEditOpen(false);
      setEditingId(null);
      refreshAll();
    } catch (error: any) {
      toast.error(error.response?.data?.message || 'Не удалось обновить поступление');
    }
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    try {
      await cashflowApi.deleteReceipt(deleteTarget.id);
      toast.success('Поступление удалено');
      setDeleteTarget(null);
      refreshAll();
    } catch (error: any) {
      toast.error(error.response?.data?.message || 'Не удалось удалить поступление');
    }
  };

  // ─── derived ───────────────────────────────────────────────────────
  const metricByProvider = useMemo(() => {
    const map = new Map<CashflowProvider, ProviderMetrics>();
    summary?.providers.forEach((p) => map.set(p.provider, p));
    return map;
  }, [summary]);

  const comparisonData = useMemo(
    () =>
      PROVIDER_ORDER.map((p) => {
        const m = metricByProvider.get(p);
        return {
          name: PROVIDER_LABELS[p],
          Чистое: m?.net ?? 0,
          Поступило: m?.received ?? 0,
          Осталось: m?.remaining ?? 0,
        };
      }),
    [metricByProvider],
  );

  const timeseriesData = summary?.timeseries ?? [];

  const cardCls = cn(
    'rounded-2xl border-0 p-5',
    isDark ? 'bg-[#1E293B] shadow-lg shadow-black/10' : 'bg-white shadow-lg shadow-slate-200/50',
  );
  const labelCls = cn('text-xs', isDark ? 'text-slate-400' : 'text-slate-500');
  const chartGrid = isDark ? '#334155' : '#E2E8F0';
  const tooltipStyle = isDark
    ? { backgroundColor: '#1E293B', border: '1px solid #334155', borderRadius: 12, color: '#fff' }
    : { backgroundColor: '#fff', border: '1px solid #E2E8F0', borderRadius: 12 };

  // Карточка одной метрики провайдера (или общая)
  const MetricCard = ({
    title,
    commission,
    gross,
    net,
    received,
    remaining,
    accent,
  }: {
    title: string;
    commission: number | null;
    gross: number;
    net: number;
    received: number;
    remaining: number;
    accent?: boolean;
  }) => (
    <Card
      className={cn(
        cardCls,
        accent && (isDark ? 'ring-1 ring-[#0EA5A4]/40' : 'ring-1 ring-[#0EA5A4]/30'),
      )}
    >
      <div className="flex items-center justify-between mb-4">
        <span className={cn('font-semibold', isDark ? 'text-white' : 'text-[#0F172A]')}>{title}</span>
        {commission !== null && (
          <Badge className="rounded-lg border-0 bg-slate-500/10 text-slate-500 font-medium">
            {formatPercent(commission)}
          </Badge>
        )}
      </div>
      <div className="space-y-3">
        <div className="flex items-center justify-between">
          <span className={labelCls}>Собрано</span>
          <span className={cn('font-semibold tabular-nums', isDark ? 'text-white' : 'text-[#0F172A]')}>
            {formatMoney(gross)}
          </span>
        </div>
        <div className="flex items-center justify-between">
          <span className={labelCls}>Чистое</span>
          <span className="font-semibold tabular-nums text-emerald-500">{formatMoney(net)}</span>
        </div>
        <div className="flex items-center justify-between">
          <span className={labelCls}>Поступило</span>
          <span className={cn('font-medium tabular-nums', isDark ? 'text-slate-200' : 'text-slate-700')}>
            {formatMoney(received)}
          </span>
        </div>
        <div className={cn('flex items-center justify-between pt-2 border-t', isDark ? 'border-slate-700' : 'border-slate-100')}>
          <span className={labelCls}>Осталось получить</span>
          <span
            className={cn(
              'font-semibold tabular-nums',
              remaining > 0 ? 'text-amber-500' : 'text-emerald-500',
            )}
          >
            {formatMoney(remaining)}
          </span>
        </div>
      </div>
    </Card>
  );

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4">
        <div>
          <h1 className={cn('text-2xl lg:text-3xl font-bold tracking-tight flex items-center gap-2', isDark ? 'text-white' : 'text-[#0F172A]')}>
            <Wallet className="w-7 h-7 text-[#0EA5A4]" />
            Cashflow
          </h1>
          <p className={cn('text-sm mt-1', isDark ? 'text-slate-400' : 'text-slate-500')}>
            Сверка: собрано провайдерами → чистое после комиссии → поступило на счёт → осталось получить
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-3">
          <Select value={period} onValueChange={(v: CashflowPeriod) => { setPeriod(v); setPage(1); }}>
            <SelectTrigger className={cn('w-[150px] h-10 rounded-xl border-0', isDark ? 'bg-slate-700 text-white' : 'bg-slate-100 text-slate-900')}>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {PERIOD_OPTIONS.map((o) => (
                <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          {period === 'range' && (
            <div className="flex items-center gap-2">
              <Input type="date" value={rangeFrom} onChange={(e) => { setRangeFrom(e.target.value); setPage(1); }}
                className={cn('h-10 rounded-xl border-0', isDark ? 'bg-slate-700 text-white' : 'bg-slate-100 text-slate-900')} />
              <span className={labelCls}>—</span>
              <Input type="date" value={rangeTo} onChange={(e) => { setRangeTo(e.target.value); setPage(1); }}
                className={cn('h-10 rounded-xl border-0', isDark ? 'bg-slate-700 text-white' : 'bg-slate-100 text-slate-900')} />
            </div>
          )}
          <Button onClick={openCreate} className="bg-[#0EA5A4] hover:bg-[#0EA5A4]/90 h-10">
            <Plus className="w-4 h-4 mr-2" />
            Добавить поступление
          </Button>
        </div>
      </div>

      {/* Metric cards */}
      {summaryLoading || !summary ? (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="h-8 w-8 animate-spin text-[#0EA5A4]" />
        </div>
      ) : (
        <>
          <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-4">
            {PROVIDER_ORDER.map((p) => {
              const m = metricByProvider.get(p);
              return (
                <MetricCard
                  key={p}
                  title={PROVIDER_LABELS[p]}
                  commission={m?.commission ?? 0}
                  gross={m?.gross ?? 0}
                  net={m?.net ?? 0}
                  received={m?.received ?? 0}
                  remaining={m?.remaining ?? 0}
                />
              );
            })}
            <MetricCard
              title="Общий"
              commission={null}
              gross={summary.total.gross}
              net={summary.total.net}
              received={summary.total.received}
              remaining={summary.total.remaining}
              accent
            />
          </div>

          {/* Charts */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            <Card className={cardCls}>
              <h3 className={cn('text-lg font-semibold mb-4', isDark ? 'text-white' : 'text-[#0F172A]')}>
                По провайдерам
              </h3>
              <ResponsiveContainer width="100%" height={280}>
                <BarChart data={comparisonData}>
                  <CartesianGrid strokeDasharray="3 3" stroke={chartGrid} vertical={false} />
                  <XAxis dataKey="name" stroke={isDark ? '#94A3B8' : '#64748B'} fontSize={12} />
                  <YAxis stroke={isDark ? '#94A3B8' : '#64748B'} fontSize={12} width={80}
                    tickFormatter={(v) => new Intl.NumberFormat('ru-RU', { notation: 'compact' }).format(Number(v))} />
                  <Tooltip contentStyle={tooltipStyle} formatter={(v: any) => formatMoney(Number(v))} />
                  <Legend />
                  <Bar dataKey="Чистое" fill="#10B981" radius={[6, 6, 0, 0]} />
                  <Bar dataKey="Поступило" fill="#0EA5A4" radius={[6, 6, 0, 0]} />
                  <Bar dataKey="Осталось" fill="#F59E0B" radius={[6, 6, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </Card>

            <Card className={cardCls}>
              <h3 className={cn('text-lg font-semibold mb-4', isDark ? 'text-white' : 'text-[#0F172A]')}>
                Поступления по времени
              </h3>
              {timeseriesData.length === 0 ? (
                <div className={cn('flex items-center justify-center h-[280px]', labelCls)}>
                  Нет поступлений за выбранный период
                </div>
              ) : (
                <ResponsiveContainer width="100%" height={280}>
                  <LineChart data={timeseriesData}>
                    <CartesianGrid strokeDasharray="3 3" stroke={chartGrid} vertical={false} />
                    <XAxis dataKey="date" stroke={isDark ? '#94A3B8' : '#64748B'} fontSize={12} />
                    <YAxis stroke={isDark ? '#94A3B8' : '#64748B'} fontSize={12} width={80}
                      tickFormatter={(v) => new Intl.NumberFormat('ru-RU', { notation: 'compact' }).format(Number(v))} />
                    <Tooltip contentStyle={tooltipStyle} formatter={(v: any) => formatMoney(Number(v))} />
                    <Legend />
                    {PROVIDER_ORDER.map((p) => (
                      <Line key={p} type="monotone" dataKey={p} name={PROVIDER_LABELS[p]}
                        stroke={PROVIDER_COLORS[p]} strokeWidth={2} dot={false} />
                    ))}
                  </LineChart>
                </ResponsiveContainer>
              )}
            </Card>
          </div>
        </>
      )}

      {/* Receipts table */}
      <Card className={cn('rounded-2xl border-0 overflow-hidden', isDark ? 'bg-[#1E293B] shadow-lg shadow-black/10' : 'bg-white shadow-lg shadow-slate-200/50')}>
        <div className={cn('p-5 lg:p-6 border-b flex flex-col sm:flex-row sm:items-center justify-between gap-4', isDark ? 'border-slate-700' : 'border-slate-200')}>
          <div>
            <h3 className={cn('text-lg font-semibold', isDark ? 'text-white' : 'text-[#0F172A]')}>Поступления</h3>
            <p className={cn('text-sm mt-1', isDark ? 'text-slate-400' : 'text-slate-500')}>{total} записей</p>
          </div>
          <Select value={providerFilter} onValueChange={(v: 'all' | CashflowProvider) => { setProviderFilter(v); setPage(1); }}>
            <SelectTrigger className={cn('w-full sm:w-[160px] h-10 rounded-xl border-0', isDark ? 'bg-slate-700 text-white' : 'bg-slate-100 text-slate-900')}>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Все провайдеры</SelectItem>
              {PROVIDER_ORDER.map((p) => (
                <SelectItem key={p} value={p}>{PROVIDER_LABELS[p]}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <div className="overflow-x-auto">
          {receiptsLoading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="h-8 w-8 animate-spin text-[#0EA5A4]" />
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow className={cn(isDark ? 'border-slate-700' : 'border-slate-100')}>
                  <TableHead className={cn('font-semibold', isDark ? 'text-slate-400' : 'text-slate-500')}>Дата</TableHead>
                  <TableHead className={cn('font-semibold', isDark ? 'text-slate-400' : 'text-slate-500')}>Провайдер</TableHead>
                  <TableHead className={cn('font-semibold text-right', isDark ? 'text-slate-400' : 'text-slate-500')}>Сумма</TableHead>
                  <TableHead className={cn('font-semibold hidden md:table-cell', isDark ? 'text-slate-400' : 'text-slate-500')}>Комментарий</TableHead>
                  <TableHead className={cn('font-semibold text-right', isDark ? 'text-slate-400' : 'text-slate-500')}>Действия</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {receipts.length === 0 ? (
                  <TableRow><TableCell colSpan={5} className="text-center py-12 text-slate-500">Поступлений нет</TableCell></TableRow>
                ) : receipts.map((r) => (
                  <TableRow key={r.id} className={cn('transition-colors', isDark ? 'border-slate-700 hover:bg-slate-800/50' : 'border-slate-100 hover:bg-slate-50')}>
                    <TableCell className={cn(isDark ? 'text-slate-300' : 'text-slate-700')}>
                      {new Date(r.date).toLocaleDateString('ru-RU', { year: 'numeric', month: 'short', day: 'numeric' })}
                    </TableCell>
                    <TableCell>
                      <Badge className="rounded-lg border-0 font-medium" style={{ backgroundColor: `${PROVIDER_COLORS[r.provider]}1A`, color: PROVIDER_COLORS[r.provider] }}>
                        {PROVIDER_LABELS[r.provider]}
                      </Badge>
                    </TableCell>
                    <TableCell className={cn('text-right font-semibold tabular-nums', isDark ? 'text-white' : 'text-[#0F172A]')}>
                      {formatMoney(r.amount)}
                    </TableCell>
                    <TableCell className={cn('hidden md:table-cell max-w-xs truncate', isDark ? 'text-slate-400' : 'text-slate-500')}>
                      {r.comment || '—'}
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center justify-end gap-1">
                        <Button variant="ghost" size="sm" onClick={() => openEdit(r)}
                          className={cn('h-8 px-2 rounded-lg', isDark ? 'text-slate-400 hover:bg-slate-800 hover:text-white' : 'text-slate-600 hover:bg-slate-100')}>
                          <Pencil className="w-4 h-4" />
                        </Button>
                        <Button variant="ghost" size="sm" onClick={() => setDeleteTarget(r)}
                          className="h-8 px-2 rounded-lg text-red-500 hover:bg-red-500/10 hover:text-red-600">
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </div>

        {!receiptsLoading && receipts.length > 0 && (
          <Pagination isDark={isDark} currentPage={page} totalPages={totalPages} total={total} itemsPerPage={ITEMS_PER_PAGE} onPageChange={setPage} />
        )}
      </Card>

      {/* Create / Edit dialogs */}
      <ReceiptDialog
        isDark={isDark}
        isOpen={isCreateOpen}
        onOpenChange={setIsCreateOpen}
        formData={formData}
        onFormDataChange={setFormData}
        onSubmit={handleCreate}
        title="Новое поступление"
        description="Деньги, реально пришедшие на счёт от провайдера"
        submitLabel="Добавить"
      />
      <ReceiptDialog
        isDark={isDark}
        isOpen={isEditOpen}
        onOpenChange={setIsEditOpen}
        formData={formData}
        onFormDataChange={setFormData}
        onSubmit={handleUpdate}
        title="Редактировать поступление"
        description="Изменить запись о поступлении"
        submitLabel="Сохранить"
      />

      {/* Delete confirm */}
      <AlertDialog open={!!deleteTarget} onOpenChange={(open) => !open && setDeleteTarget(null)}>
        <AlertDialogContent className={cn(isDark ? 'bg-[#1E293B] border-slate-700' : 'bg-white')}>
          <AlertDialogHeader>
            <AlertDialogTitle className={cn(isDark ? 'text-white' : 'text-[#0F172A]')}>Удалить поступление?</AlertDialogTitle>
            <AlertDialogDescription className={cn(isDark ? 'text-slate-400' : 'text-slate-500')}>
              {deleteTarget && (
                <>
                  {PROVIDER_LABELS[deleteTarget.provider]} · {formatMoney(deleteTarget.amount)} ·{' '}
                  {new Date(deleteTarget.date).toLocaleDateString('ru-RU')}. Действие необратимо.
                </>
              )}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className={cn(isDark ? 'border-slate-700 text-slate-300 hover:bg-slate-800' : 'border-slate-200')}>
              Отмена
            </AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete} className="bg-red-500 hover:bg-red-600">
              Удалить
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
